import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Filter, Trash2, Dices, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { TableBadges } from '../TableBadges';
import { TablesManager, MemberTablesSelect } from './TablesControls';

const MembersTab = ({ 
  members, 
  roleFilter, 
  setRoleFilter, 
  isAdmin, 
  isSuperAdmin, 
  getAvatarUrl,
  handleUpdateRole,
  handleUpdateBureauRole,
  handleUpdatePlayerStatus,
  handleDeleteMember,
  getRoleBadgeClass,
  tables = [],
  setTables,
  handleUpdateTables
}) => {
  const bureauRoles = [
    { value: 'none', label: 'Aucun rôle bureau' },
    { value: 'president', label: 'Président(e)' },
    { value: 'vice_president', label: 'Vice-Président(e)' },
    { value: 'secretary', label: 'Secrétaire' },
    { value: 'treasurer', label: 'Trésorier(e)' },
    { value: 'member', label: 'Membre du bureau' }
  ];

  const playerStatuses = [
    { value: 'none', label: 'Non défini', color: 'text-gray-400' },
    { value: 'roliste', label: 'Rôliste', color: 'text-blue-400' },
    { value: 'mj', label: 'Meneur de Jeu', color: 'text-orange-400' },
    { value: 'both', label: 'Rôliste & Meneur', color: 'text-green-400' }
  ];

  const getPlayerStatusBadge = (status) => {
    if (!status) return null;
    const statusConfig = {
      roliste: { label: 'Rôliste', class: 'bg-blue-600/50 border-blue-400/50 text-blue-200' },
      mj: { label: 'Meneur', class: 'bg-orange-600/50 border-orange-400/50 text-orange-200' },
      both: { label: 'Rôliste & Meneur', class: 'bg-green-600/50 border-green-400/50 text-green-200' }
    };
    const config = statusConfig[status];
    if (!config) return null;
    return (
      <Badge className={`${config.class} text-xs`}>
        {status === 'mj' || status === 'both' ? <Crown className="h-3 w-3 mr-1" /> : <Dices className="h-3 w-3 mr-1" />}
        {config.label}
      </Badge>
    );
  };

  return (
    <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-4 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-purple-300">Membres de l&apos;association</h2>
        
        {/* Role Filter + tables manager */}
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin() && <TablesManager tables={tables} onUpdated={setTables} />}
          <Filter className="h-4 w-4 text-purple-400" />
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px] bg-black/50 border-purple-500/30 text-purple-300">
              <SelectValue placeholder="Filtrer par rôle" />
            </SelectTrigger>
            <SelectContent className="bg-black border-purple-500/30">
              <SelectItem value="all" className="text-purple-300">Tous les rôles</SelectItem>
              {isSuperAdmin && isSuperAdmin() && (
                <SelectItem value="superadmin" className="text-yellow-400">Super Admin</SelectItem>
              )}
              <SelectItem value="admin" className="text-red-400">Administrateurs</SelectItem>
              <SelectItem value="member" className="text-green-400">Membres</SelectItem>
              <SelectItem value="guest" className="text-gray-400">Invités</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {members.map((member) => (
          <div key={member.user_id} className="flex items-center justify-between gap-2 p-3 sm:p-4 bg-black/30 rounded-lg border border-purple-500/20 hover:border-purple-400/40 transition-all min-w-0">
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
              {member.avatar ? (
                <img 
                  src={getAvatarUrl(member.avatar)} 
                  alt={member.name} 
                  className="h-12 w-12 sm:h-16 sm:w-16 rounded-full border-2 border-purple-500/50 object-cover flex-shrink-0"
                />
              ) : (
                <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-purple-600 flex items-center justify-center text-lg sm:text-xl font-bold text-white flex-shrink-0">
                  {member.name.charAt(0)}
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link 
                    to={`/member/${member.user_id}`}
                    className="text-base sm:text-lg font-bold text-purple-200 hover:text-purple-100 transition-colors hover:underline"
                  >
                    {member.name}
                  </Link>
                  {!isAdmin() && getPlayerStatusBadge(member.player_status)}
                </div>
                <TableBadges tableIds={member.tables} tables={tables} className="mt-1" />
                {/* Email visible only to admins */}
                {isAdmin() && (
                  <p className="text-xs sm:text-sm text-gray-400 break-all">{member.email}</p>
                )}
                {member.discord_username && (
                  <p className="text-xs text-purple-400">Discord: {member.discord_username}</p>
                )}
                {isAdmin() ? (
                  <div className="flex flex-col gap-2 mt-2">
                    {/* Role selector */}
                    <Select
                      value={member.role}
                      onValueChange={(value) => handleUpdateRole(member.user_id, value)}
                    >
                      <SelectTrigger className="w-[150px] sm:w-[180px] bg-black/50 border-purple-500/30 text-purple-300 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-purple-500/30">
                        <SelectItem value="guest" className="text-gray-400">Invité</SelectItem>
                        <SelectItem value="member" className="text-green-400">Membre</SelectItem>
                        <SelectItem value="admin" className="text-red-400">Admin</SelectItem>
                        {isSuperAdmin && isSuperAdmin() && (
                          <SelectItem value="superadmin" className="text-yellow-400">Super Admin</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {/* Bureau role selector */}
                    <Select
                      value={member.bureau_role || 'none'}
                      onValueChange={(value) => handleUpdateBureauRole(member.user_id, value === 'none' ? null : value)}
                    >
                      <SelectTrigger className="w-[150px] sm:w-[180px] bg-black/50 border-purple-500/30 text-purple-300 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-purple-500/30">
                        {bureauRoles.map(role => (
                          <SelectItem key={role.value} value={role.value} className="text-purple-300">
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* Player status selector */}
                    <Select
                      value={member.player_status || 'none'}
                      onValueChange={(value) => handleUpdatePlayerStatus(member.user_id, value === 'none' ? null : value)}
                    >
                      <SelectTrigger className="w-[150px] sm:w-[180px] bg-black/50 border-purple-500/30 text-purple-300 text-sm">
                        <SelectValue placeholder="Statut joueur" />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-purple-500/30">
                        {playerStatuses.map(status => (
                          <SelectItem key={status.value} value={status.value} className={status.color}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* Tables multi-select */}
                    <MemberTablesSelect member={member} tables={tables} onChange={handleUpdateTables} />
                  </div>
                ) : (
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border mt-2 ${getRoleBadgeClass(member.role)}`}>
                    {member.role === 'superadmin' ? 'Super Admin' : 
                     member.role === 'admin' ? 'Admin' : 
                     member.role === 'member' ? 'Membre' : 'Invité'}
                  </span>
                )}
              </div>
            </div>
            {isAdmin() && member.role !== 'superadmin' && (
              <Button
                variant="destructive"
                size="icon"
                onClick={() => handleDeleteMember(member.user_id)}
                className="ml-2 flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default MembersTab;
